/* ──────────────────────────────────────────────────────────────
   MetricDetail.jsx
   Full-screen sheet that shows one metric in detail
   ────────────────────────────────────────────────────────────── */
import React            from 'react';
import PropTypes        from 'prop-types';
import { IoCloseSharp } from 'react-icons/io5';
import { FaRegHeart }   from 'react-icons/fa6';      // fallback icon
import './MetricDetail.scss';

/* ---------- helpers ---------- */
/** pick colour + qualitative-band width from status  */
const cfgForStatus = (status, fallbackPct) => {
  const lut = {
    poor        : { col: '#FF5A5F', band: 30 },
    satisfactory: { col: '#FFC400', band: 65 },
    good        : { col: '#35C759', band: 100 }
  };
  return lut[status?.toLowerCase()] ?? { col: '#0067ff', band: fallbackPct };
};

/** circumference for r = 42 (track circle) */
const CIRC = 2 * Math.PI * 42;   // ≈ 263.89

export default function MetricDetail({ data, onClose }) {
  const {
    label,
    value,
    unit     = '',
    status   = 'unknown',   // poor | satisfactory | good | unknown
    percent  = 0,           // 0-100
    icon     = <FaRegHeart />,
    date     = new Date().toLocaleDateString(),
    time     = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    color    // optional override
  } = data;

  /* colour & qualitative band */
  const cfg   = cfgForStatus(status, percent);
  const col   = cfg.col;
  const bandW = cfg.band;

  /* donut stroke */
  const dash = `${(percent / 100) * CIRC} ${CIRC}`;

  /* ---------- render ---------- */
  return (
    <div className="metric-detail">
      {/* close bar */}
      <header className="top">
        <button className="close" onClick={onClose}>
          <IoCloseSharp />
        </button>
      </header>

      {/* main scroll area */}
      <div className="scroll-body">
        <h1 style={{ color: col }}>{label}</h1>
        <span className="fav">{icon}</span>

        <div className="meta">
          <span>{date}</span>
          <span>{time}</span>
        </div>

        {/* ── Summary card ───────────────────────────── */}
        <section className="summary card" style={{ '--c': col }}>
          <div className="summary__text">
            <span className="value">{value}</span>
            {unit && <span className="unit">{unit}</span>}
            <span className="status">{status.toUpperCase()}</span>
          </div>

          {/* donut gauge */}
          <svg viewBox="0 0 100 100" className="gauge">
            <circle cx="50" cy="50" r="42" className="track" />
            <circle
              cx="50" cy="50" r="42"
              className="bar"
              strokeDasharray={dash}
            />
            <text x="50" y="55" textAnchor="middle" className="pct" transform="rotate(90,50,50)">
              {percent}%
            </text>
          </svg>
        </section>

        {/* ── Qualitative band ───────────────────────── */}
        <section className="band card" style={{ '--c': col }}>
          <div className="labels">
            <span>Poor</span><span>Satisfactory</span><span>Good</span>
          </div>
          <div className="bar-bg">
            <div
              className="bar-fill"
              style={{ width: `${bandW}%` }}
            />
          </div>
        </section>

        {/* ── Info blocks (placeholder copy) ─────────── */}
        <section className="info card">
          <h3>What is {label}?</h3>
          <p>
            {label} is a key health indicator. By monitoring it over time you
            can spot early warning signs and keep track of lifestyle changes.
          </p>
        </section>

        <section className="info card">
          <h3>How to improve?</h3>
          <p>
            Maintain a balanced diet, exercise regularly, manage stress, and
            consult your healthcare provider if readings fall outside the
            recommended range.
          </p>
        </section>

        <div style={{ height: 24 }} />
      </div>
    </div>
  );
}

MetricDetail.propTypes = {
  /* data describing one metric */
  data: PropTypes.shape({
    label  : PropTypes.string.isRequired,
    value  : PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    unit   : PropTypes.string,
    status : PropTypes.string,      // poor | satisfactory | good
    percent: PropTypes.number,      // 0-100
    icon   : PropTypes.node,        // optional React element
    color  : PropTypes.string,      // override colour
    date   : PropTypes.string,
    time   : PropTypes.string
  }).isRequired,
  /* callback to close the sheet */
  onClose: PropTypes.func.isRequired
};
